import { dayNumber, diasEntre, parseISODate, type CalendarDate } from './calendar';
import { hoyCaracas } from './freshness';
import { unidadLabel } from './labels';
import { roundTo } from './numbers';
import type { ISODate, Lang } from './types';

export { parseDecimal } from './parseDecimal';
export { roundTo } from './numbers';

/**
 * Formato de pantalla (es-VE / en). Reglas de la arquitectura:
 *  - decimales con coma en español y miles SIEMPRE agrupados ('1.500,50'); no dependemos del ICU por
 *    defecto (en `es` no agrupa 4 cifras): `useGrouping: 'always'` y redondeo propio antes de Intl,
 *  - signo menos tipográfico (U+2212) y "+" explícito cuando se pide `sign`,
 *  - fechas 'YYYY-MM-DD' como fechas de calendario (calendar.ts), meses con tablas propias para no
 *    depender de cómo abrevie cada versión de ICU ("oct." vs "oct").
 *  - sin dato (null/NaN/fecha inválida) → '—'.
 */
export const EMPTY = '—';
export const MINUS = '−';

export interface NumberOptions {
  min?: number;
  max?: number;
  sign?: boolean;
  lang?: Lang;
}
export interface MoneyOptions {
  decimals?: number;
  sign?: boolean;
  lang?: Lang;
}

const formatters = new Map<string, Intl.NumberFormat>();

function numberFormat(lang: Lang, min: number, max: number): Intl.NumberFormat {
  const key = `${lang}|${min}|${max}`;
  let format = formatters.get(key);
  if (!format) {
    format = new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'es-VE', {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
      useGrouping: 'always',
    });
    formatters.set(key, format);
  }
  return format;
}

const clampDigits = (n: number | undefined, fallback: number): number =>
  Number.isInteger(n) ? Math.min(8, Math.max(0, n as number)) : fallback;

interface Rendered {
  body: string;
  prefix: '' | '+' | typeof MINUS;
}

function render(value: number, min: number, max: number, lang: Lang, sign: boolean): Rendered {
  const rounded = roundTo(value, max);
  const prefix = rounded < 0 ? MINUS : sign && rounded > 0 ? '+' : '';
  return { body: numberFormat(lang, min, max).format(Math.abs(rounded)), prefix };
}

const isNum = (n: number | null | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n);

export function formatNumber(n: number | null | undefined, opts: NumberOptions = {}): string {
  if (!isNum(n)) return EMPTY;
  const max = clampDigits(opts.max, 2);
  const min = Math.min(clampDigits(opts.min, 0), max);
  const { prefix, body } = render(n, min, max, opts.lang ?? 'es', opts.sign ?? false);
  return prefix + body;
}

/** `$2,55` (2 decimales por defecto; admite 3 para precios por unidad pequeña). */
export function formatUsd(n: number | null | undefined, opts: MoneyOptions = {}): string {
  if (!isNum(n)) return EMPTY;
  const d = clampDigits(opts.decimals, 2);
  const { prefix, body } = render(n, d, d, opts.lang ?? 'es', opts.sign ?? false);
  return `${prefix}$${body}`;
}

/** `Bs. 2.014,00` */
export function formatBs(n: number | null | undefined, opts: MoneyOptions = {}): string {
  if (!isNum(n)) return EMPTY;
  const d = clampDigits(opts.decimals, 2);
  const { prefix, body } = render(n, d, d, opts.lang ?? 'es', opts.sign ?? false);
  return `${prefix}Bs. ${body}`;
}

/** `+4,2 %` (es) · `+4.2%` (en). El signo se decide tras redondear: −0,04 → "0,0 %". */
export function formatPct(n: number | null | undefined, opts: MoneyOptions = {}): string {
  if (!isNum(n)) return EMPTY;
  const d = clampDigits(opts.decimals, 1);
  const lang = opts.lang ?? 'es';
  const { prefix, body } = render(n, d, d, lang, opts.sign ?? false);
  return `${prefix}${body}${lang === 'en' ? '%' : ' %'}`;
}

/** Cantidad con su unidad: '1.250 kg', '2,5 L', '1 unidad', '3 unidades'. */
export function formatQuantity(
  n: number | null | undefined,
  unidad: string | null | undefined,
  lang: Lang = 'es',
): string {
  if (!isNum(n)) return EMPTY;
  const cantidad = formatNumber(n, { max: 2, lang });
  const etiqueta = unidadLabel(unidad, lang, n);
  return etiqueta ? `${cantidad} ${etiqueta}` : cantidad;
}

/* ───────────────────────────────── Fechas ───────────────────────────────── */

const MESES = {
  es: {
    corto: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    largo: [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ],
  },
  en: {
    corto: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    largo: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
  },
} as const;

export type EstiloFecha = 'corta' | 'media' | 'larga';

const mes = (lang: Lang, largo: boolean, m: number): string =>
  MESES[lang][largo ? 'largo' : 'corto'][m - 1] ?? '';

function describe(p: CalendarDate, estilo: EstiloFecha, lang: Lang): string {
  if (lang === 'en') {
    const base = `${mes('en', estilo === 'larga', p.m)} ${p.d}`;
    return estilo === 'corta' ? base : `${base}, ${p.y}`;
  }
  if (estilo === 'larga') return `${p.d} de ${mes('es', true, p.m)} de ${p.y}`;
  const base = `${p.d} ${mes('es', false, p.m)}`;
  return estilo === 'media' ? `${base} ${p.y}` : base;
}

/** corta: '12 oct' · media: '12 oct 2026' · larga: '12 de octubre de 2026'. */
export function formatDate(
  iso: ISODate | null | undefined,
  estilo: EstiloFecha = 'corta',
  lang: Lang = 'es',
): string {
  const parts = parseISODate(iso);
  return parts ? describe(parts, estilo, lang) : EMPTY;
}

/** '12–16 oct 2026' · '28 sep – 2 oct 2026' · '28 dic 2026 – 3 ene 2027' · sin fin: '12 oct 2026'. */
export function formatDateRange(
  fecha: ISODate | null | undefined,
  fechaFin?: ISODate | null,
  lang: Lang = 'es',
): string {
  const first = parseISODate(fecha);
  const last = parseISODate(fechaFin);
  if (!first) return EMPTY;
  if (!last || dayNumber(first) === dayNumber(last)) return describe(first, 'media', lang);
  const [a, b] = dayNumber(first) <= dayNumber(last) ? [first, last] : [last, first];
  if (a.y !== b.y) return `${describe(a, 'media', lang)} – ${describe(b, 'media', lang)}`;
  if (a.m !== b.m) return `${describe(a, 'corta', lang)} – ${describe(b, 'media', lang)}`;
  return lang === 'en'
    ? `${mes('en', false, a.m)} ${a.d}–${b.d}, ${a.y}`
    : `${a.d}–${b.d} ${mes('es', false, a.m)} ${a.y}`;
}

/** 'hoy', 'ayer', 'mañana', 'hace 3 días', 'en 2 días' (en: today, yesterday, tomorrow, 3 days ago, in 2 days). */
export function formatRelativeDays(
  iso: ISODate | null | undefined,
  hoy: ISODate = hoyCaracas(),
  lang: Lang = 'es',
): string {
  const n = iso ? diasEntre(hoy, iso) : Number.NaN;
  if (Number.isNaN(n)) return EMPTY;
  const abs = Math.abs(n);
  if (lang === 'en') {
    if (n === 0) return 'today';
    if (abs === 1) return n < 0 ? 'yesterday' : 'tomorrow';
    return n < 0 ? `${abs} days ago` : `in ${abs} days`;
  }
  if (n === 0) return 'hoy';
  if (abs === 1) return n < 0 ? 'ayer' : 'mañana';
  return n < 0 ? `hace ${abs} días` : `en ${abs} días`;
}
